import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import Table from 'reactstrap/lib/Table';
import { defaultTo, get } from 'lodash-es';
import { useSafeId } from 'site-modules/shared/hooks/use-safe-id';
import { useTableSort, SORT_ORDER } from 'site-modules/shared/hooks/editorial/use-table-sort';
import { TrackingConstant } from 'client/tracking/constant';
import { CmsEntities } from 'client/data/models/cms';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { ShareThisChart } from 'client/site-modules/shared/components/share-this-chart/share-this-chart';
import { CtaButtonText } from 'site-modules/shared/components/native-ad/native-ad-components/cta-button-text/cta-button-text';
import { AdUnit } from 'site-modules/shared/components/ad-unit/ad-unit';
import { ScrollLink } from 'site-modules/shared/components/scroll-link/scroll-link';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { bootstrapMigrationLeftRightToStartEnd } from 'client/utils/bootstrap/map-align';
import { ArticleTableTooltip } from 'site-modules/shared/components/editorial/article-table-tooltip/article-table-tooltip';
import { compareValues, getTablePropsFromContent } from './article-table-helpers';

import './article-table.scss';

const POSSIBLE_ALIGN = ['left', 'right', 'center'];

export function ArticleTable({
  entry = DEFAULT_CONTENT,
  className = null,
  tableClassName = null,
  shareOptions = {},
  isMobile = false,
  shareTableHeader = false,
}) {
  const id = `table-${useSafeId()}`;
  const { canonical, pageTitle, contentId } = shareOptions;
  const entryPath = defaultTo(shareOptions.entryPath, []);
  const {
    title,
    creativeId,
    TitleTag,
    adName,
    articleClassName: articleClassNameFromContent,
    tableClassName: tableClassNameFromContent,
    stickyFirstColumn,
    rowCount,
    columnCount,
    renderColumnHeader,
    columnCategoriesBackgrounds,
    hasCategories,
    columnHeaderBackgrounds,
    columnWidths,
    fontSize,
    noVerticalBorders,
    noRowHeaders,
    isStriped,
    isSmall,
    columnsAlign,
    headersAlign,
    hasVerticalAlignment,
    categoriesStartIndexes,
    columnCategories,
    verticalAlign,
    logo,
    inlineLogo,
    oemClickOutBtnPosition,
    oemClickOutBtnColor,
    oemClickOutText,
    oemClickOutUrl,
    oemDisplayUrl,
    disclaimer,
  } = getTablePropsFromContent(entry, isMobile);
  const initialRowValues = new Array(rowCount).fill().map((value, index) =>
    entry
      .metadata(`row${index + 1}Values`)
      .value()
      .split('|')
  );
  const initialSortColumn = new Array(columnCount)
    .fill()
    .map((v, index) => index)
    .find(columnIndex => !!entry.metadata(`columnSort${columnIndex + 1}Initial`).value());
  const initialSortOrder = entry.metadata(`columnSort${initialSortColumn + 1}Initial`).value();

  const [rowValues, setRowValues] = useState(
    initialSortOrder
      ? initialRowValues.sort((a, b) =>
          compareValues(a[initialSortColumn], b[initialSortColumn], initialSortOrder === SORT_ORDER.DESCENDING)
        )
      : initialRowValues
  );

  const [sortColumn, sortOrder, onColumnSort] = useTableSort({
    initialSortColumn,
    initialSortOrder,
    rowValues,
    setRowValues,
  });

  const [tableSizes, setTableSizes] = useState({});
  const ref = useRef();

  useEffect(() => {
    setTableSizes({ height: +get(ref, 'current.clientHeight', 0), width: +get(ref, 'current.clientWidth', 0) });
  }, []);

  if (!columnCount) {
    return null;
  }

  const skipId = `skip-table-${id}`;

  const ADS_PROPS = {
    // desktop
    false: { adName, md: true, lg: true, xl: true },
    // mobile
    true: { adName: `m${adName}`, xs: true, sm: true },
  };

  const isInlineLogo = inlineLogo && !isMobile;

  return (
    <div ref={ref} data-tracking-parent={creativeId}>
      {!shareTableHeader && (
        <ShareThisChart
          pageTitle={pageTitle}
          pageUrl={canonical}
          shareText="Share this table"
          shareableWidgetData={{
            backLink: canonical,
            defaultTitle: pageTitle,
            widgetName: 'tables',
            hostPageOverride: 'https://www.edmunds.com/widgets/tables/',
            queryParams: { contentId, entryPath: entryPath.join(',') },
            defaultWidth: tableSizes.width,
            defaultHeight: tableSizes.height,
          }}
          className="text-primary-darker text-end ms-auto rounded"
        />
      )}
      <div
        style={{ fontSize }}
        className={classnames(className, articleClassNameFromContent, `table-col-count-${columnCount}`)}
      >
        <div className={classnames('title-logo', { 'd-flex align-items-center': isInlineLogo })}>
          {!!title && (
            <TitleTag className={classnames('heading-3', { 'mb-0_5': !isInlineLogo, 'mb-0 me-0_75': isInlineLogo })}>
              {title}
            </TitleTag>
          )}
          {!!logo && (
            <img
              src={getStaticImageUrl(logo)}
              alt=""
              decoding="async"
              aria-hidden
              loading="lazy"
              className={classnames('logo', { 'mt-1 mb-1_5': !isInlineLogo })}
              style={{ maxHeight: '40px' }}
            />
          )}
        </div>
        {!!oemClickOutUrl && oemClickOutBtnPosition === 'top' && (
          <div className="table-cta-btn-container mb-1">
            <Button
              tag="a"
              color={oemClickOutBtnColor}
              size="lg"
              href={oemClickOutUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="table-cta-btn fw-medium text-transform-none px-2_5 py-0_75"
              data-tracking-id={TrackingConstant.OEM_SITE_CLICKOUT}
              data-tracking-value={oemClickOutText}
            >
              <CtaButtonText text={oemClickOutText} />
            </Button>
            {!!oemDisplayUrl && <div className="mt-0_25 text-blue-40 small">{oemDisplayUrl}</div>}
          </div>
        )}
        {shareTableHeader && (
          <ShareThisChart
            pageTitle={pageTitle}
            pageUrl={canonical}
            shareText="Share this table"
            shareableWidgetData={{
              backLink: canonical,
              defaultTitle: pageTitle,
              widgetName: 'tables',
              hostPageOverride: 'https://www.edmunds.com/widgets/tables/',
              queryParams: { contentId, entryPath: entryPath.join(',') },
              defaultWidth: tableSizes.width,
              defaultHeight: tableSizes.height,
            }}
            className="text-primary-darker text-start m-0_75"
          />
        )}
        <div className="table-container">
          <div className="table-responsive pos-r">
            <ScrollLink
              to={skipId}
              className="pos-a top-0 right-0 mt-0_5 me-0_5 rounded bg-white visually-hidden-focusable"
            >
              Skip table
            </ScrollLink>
            <Table
              striped={isStriped}
              bordered={!noVerticalBorders && !hasCategories}
              size={isSmall ? 'sm' : undefined}
              className={classnames('article-table text-gray-darker', tableClassNameFromContent, tableClassName, {
                'has-categories': hasCategories,
                'sticky-first-column': stickyFirstColumn,
              })}
            >
              {columnWidths.length === columnCount && (
                <colgroup>
                  {columnWidths.map((width, index) => {
                    const key = `${id}-colgroup-${index}`;

                    return <col key={key} width={width} />;
                  })}
                </colgroup>
              )}
              {renderColumnHeader && (
                <thead>
                  {hasCategories && (
                    <tr>
                      {columnCategories.map(({ value, colspan }, index) => {
                        const key = `${id}-category-${index}`;

                        return (
                          <th
                            key={key}
                            colSpan={colspan}
                            scope={colspan === 1 ? 'col' : 'colgroup'}
                            className={classnames('text-center align-middle', { 'category-border': !!index })}
                            {...(columnCategoriesBackgrounds[index]
                              ? { style: { background: columnCategoriesBackgrounds[index] } }
                              : {})}
                          >
                            <ContentFragment>{value}</ContentFragment>
                          </th>
                        );
                      })}
                    </tr>
                  )}
                  <tr>
                    {new Array(columnCount).fill().map((value, index) => {
                      const key = `${id}-header-${index}`;
                      const hasAlignment = POSSIBLE_ALIGN.includes(headersAlign[index]);
                      const isSortable = entry.metadata(`columnSort${index + 1}`).boolean();
                      const headerText = entry.metadata(`columnHeader${index + 1}`).value();
                      const hasTooltip = entry.metadata(`columnDescription${index + 1}`).value();
                      const defaultSortOrder = isSortable ? SORT_ORDER.NONE : null;
                      const hasCategoryBorder = !!index && categoriesStartIndexes.includes(index);
                      const headerContent = (
                        <>
                          <ContentFragment componentToUse={hasTooltip ? 'span' : 'div'}>{headerText}</ContentFragment>
                          {hasTooltip && (
                            <ArticleTableTooltip
                              descriptionText={entry.metadata(`columnDescription${index + 1}`).value()}
                            />
                          )}
                        </>
                      );

                      return (
                        <th
                          scope="col"
                          className={classnames(
                            'align-middle',
                            `text-${hasAlignment ? bootstrapMigrationLeftRightToStartEnd(headersAlign[index]) : 'center'}`,
                            {
                              'border-start': hasCategories && !!index && !hasCategoryBorder,
                              'category-border': hasCategoryBorder,
                            }
                          )}
                          key={key}
                          aria-sort={sortColumn === index ? sortOrder : defaultSortOrder}
                          {...(columnHeaderBackgrounds[index]
                            ? { style: { background: columnHeaderBackgrounds[index] } }
                            : {})}
                        >
                          {isSortable ? (
                            <button
                              className="sort-btn py-0 ps-0 pe-1_25 pos-r border-0 text-gray-darker fw-bold"
                              onClick={onColumnSort}
                              data-key={index}
                            >
                              {headerContent}
                              <i className="arrow icon-arrow-up5" aria-hidden />
                              <i className="arrow icon-arrow-down5" aria-hidden />
                            </button>
                          ) : (
                            headerContent
                          )}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
              )}
              <tbody>
                {rowValues.map((columnValues, index) => {
                  const key = `${id}-row-${index}`;

                  return (
                    <tr key={key}>
                      {columnValues.map((columnValue, columnIndex) => {
                        const columnKey = `${key}-column-${columnIndex}`;
                        const hasAlignment = POSSIBLE_ALIGN.includes(columnsAlign[columnIndex]);
                        const hasCategoryBorder = !!columnIndex && categoriesStartIndexes.includes(columnIndex);

                        let cellProps = { componentToUse: 'td' };
                        if (!noRowHeaders && !columnIndex) {
                          cellProps = { componentToUse: 'th', componentProps: { scope: 'row' } };
                        }

                        return (
                          <ContentFragment
                            {...cellProps}
                            classes={classnames({
                              [`text-${bootstrapMigrationLeftRightToStartEnd(columnsAlign[columnIndex])}`]:
                                hasAlignment,
                              [`align-${verticalAlign}`]: hasVerticalAlignment,
                              'border-start': hasCategories && !!columnIndex && !hasCategoryBorder,
                              'category-border': hasCategoryBorder,
                            })}
                            key={columnKey}
                          >
                            {columnValue}
                          </ContentFragment>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <ScrollElement id={skipId} />
          </div>
        </div>
        {!!disclaimer && <ContentFragment classes="table-disclaimer small pt-1">{disclaimer}</ContentFragment>}
        {!!oemClickOutUrl && oemClickOutBtnPosition === 'bottom' && (
          <div className="text-center mt-2">
            <Button
              tag="a"
              color={oemClickOutBtnColor}
              size="lg"
              href={oemClickOutUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="table-cta-btn fw-medium text-transform-none px-2_5 py-0_75"
              data-tracking-id={TrackingConstant.OEM_SITE_CLICKOUT}
              data-tracking-value={oemClickOutText}
            >
              <CtaButtonText text={oemClickOutText} />
            </Button>
            {!!oemDisplayUrl && <div className="mt-0_25 text-blue-40 small">{oemDisplayUrl}</div>}
          </div>
        )}
      </div>
      {!!adName && <AdUnit {...ADS_PROPS[isMobile]} position={`1`} renderWhenViewable={false} refreshable={false} />}
    </div>
  );
}

ArticleTable.propTypes = {
  entry: CmsEntities.Content,
  className: PropTypes.string,
  tableClassName: PropTypes.string,
  shareOptions: PropTypes.shape({}),
  isMobile: PropTypes.bool,
  shareTableHeader: PropTypes.bool,
};
